import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 ease-elegant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 smooth-transition active:scale-[0.98] touch-target',
  {
    variants: {
      variant: {
        default: 'bg-orange-500 text-white shadow-md hover:bg-orange-600 hover:shadow-lg',
        destructive: 'bg-red-600 text-white shadow-md hover:bg-red-800 hover:shadow-lg',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:shadow-md',
        secondary:
          'bg-white text-black shadow-sm hover:bg-gray-50 hover:shadow-md border border-gray-200',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        gradient: 'gradient-primary text-white shadow-lg hover:shadow-xl font-semibold',
        success: 'bg-success text-success-foreground shadow-md hover:bg-success/90 hover:shadow-lg',
        warning: 'bg-warning text-warning-foreground shadow-md hover:bg-warning/90 hover:shadow-lg',
        white: 'bg-white text-primary shadow-md hover:bg-gray-100 hover:shadow-lg',
      },
      size: {
        default: 'h-11 px-6 py-2.5 min-h-[44px]',
        sm: 'h-9 rounded-md px-4 text-xs min-h-[36px]',
        lg: 'h-12 rounded-md px-8 text-base font-semibold min-h-[48px]',
        xl: 'h-14 rounded-lg px-10 text-lg font-semibold min-h-[56px]',
        icon: 'h-11 w-11 min-h-[44px] min-w-[44px]',
      },
      justify: {
        default: 'justify-center',
        start: 'justify-start',
        end: 'justify-end',
        stretch: 'justify-stretch w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  justify?: 'start' | 'end' | 'stretch'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      justify,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, justify, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>{children}</span>
          </div>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
